import * as styles from "./SectionSteppedProcess.module.scss";

import React, { useState } from "react";
import clip from "text-clipper";
import { Chevron } from "../../../components/common/Icons";
import WrappedSection from "../../WrappedSection/WrappedSection";
import { ParagraphImageOrText } from "../../paragraphs/ParagraphImageOrText/ParagraphImageOrText";

const STEP_MAX_CHARACTER_COUNT = 280;

export interface SteppedProcessStep {
	title: string;
	introText: string;
	url: string;
	urlTitle: string;
	imageOrText?: Queries.ParagraphImageOrTextDataFragment[];
}
export interface SectionSteppedProcessProps {
	heading: string;
	jumpLinksTitle?: string;
	htmlIntroText?: string;
	steps: SteppedProcessStep[];
}

export const getSectionSteppedProcessProps = ({
	title,
	field_stepped_intro_text,
	relationships: { field_step },
}: Queries.node__stepped_process): SectionSteppedProcessProps => ({
	heading: title,
	htmlIntroText: field_stepped_intro_text,
	steps: field_step.map((step) => ({
		title: step.field_step_title,
		introText: step.field_step_intro_text?.value,
		url: step.field_step_link?.uri,
		urlTitle: step.field_step_link?.title,
	})),
});
export const SectionSteppedProcess: React.FC<SectionSteppedProcessProps> = ({
	heading,
	jumpLinksTitle,
	htmlIntroText,
	steps,
}) => {
	const getShortText = (step: SteppedProcessStep) => {
		if (step.introText?.length > STEP_MAX_CHARACTER_COUNT)
			return `${clip(step.introText.trim(), STEP_MAX_CHARACTER_COUNT)}`;
		return step.introText;
	};
	//show more if intro text more than max character count and there is imageOrText paras
	const canShowMore = (step: SteppedProcessStep) => {
		if (
			step.introText?.length > STEP_MAX_CHARACTER_COUNT ||
			step.imageOrText?.length > 0
		) {
			return true;
		}
		return false;
	};
	const [showAllText, setShowAllText] = useState(null);
	const toggleExpanded = (stepTitle: string) => {
		if (!showAllText || showAllText !== stepTitle) {
			return setShowAllText(stepTitle);
		}
		return setShowAllText(null);
	};

	return (
		<WrappedSection sectionName="SectionSteppedProcess">
			<div className={styles.steppedprocess}>
				<div className={styles.title}>
					{jumpLinksTitle ? (
						<h2 className={styles.heading} data-jumplinkstitle={jumpLinksTitle}>
							{heading}
						</h2>
					) : (
						<h2 className={styles.heading}>{heading}</h2>
					)}
				</div>
				{htmlIntroText && (
					<div
						className={styles.introtext}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{
							__html: `<p>${htmlIntroText}</p>`,
						}}
					/>
				)}

				<div className={styles.stepwrapper}>
					{steps.map((step, i) => (
						<div key={step.title} className={styles.step}>
							<div className={styles.stepordering}>
								<div className={styles.number}>
									<div className={styles.numbertitle}>{i + 1}</div>
									{i < steps.length - 1 && (
										<div className={styles.numberborder} />
									)}
								</div>
							</div>
							<div
								className={
									i === steps.length - 1
										? styles.stepcontentbottom
										: styles.stepcontent
								}
							>
								<h3 className={styles.steptitle}>{step.title}</h3>
								{showAllText !== step.title && (
									<div
										aria-hidden={showAllText === step.title}
										className={styles.stepintrotext}
										// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
										dangerouslySetInnerHTML={{
											__html: getShortText(step),
										}}
									/>
								)}

								<div
									aria-hidden={showAllText !== step.title}
									className={
										showAllText === step.title
											? styles.stepintrotext
											: styles.visibilityhidden
									}
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
									dangerouslySetInnerHTML={{
										__html: step.introText,
									}}
								/>
								{step.imageOrText?.length > 0 && (
									<div
										aria-hidden={showAllText !== step.title}
										className={
											showAllText !== step.title && styles.visibilityhidden
										}
									>
										{step.imageOrText?.map((imageOrTextBlock) => (
											<ParagraphImageOrText
												key={step.title}
												data={imageOrTextBlock}
											/>
										))}
									</div>
								)}

								{step.url && step.urlTitle && (
									<a
										href={step.url}
										className={
											canShowMore(step) && showAllText !== step.title
												? styles.visibilityhidden
												: styles.steplink
										}
									>
										{step.urlTitle}
									</a>
								)}

								{canShowMore(step) && showAllText !== step.title && (
									<div className={styles.gradient} />
								)}
								{canShowMore(step) && (
									<button
										type="button"
										className={styles.showmore}
										onClick={() => toggleExpanded(step.title)}
										tabIndex={0}
										onKeyDown={(e) =>
											e.key === "Enter" && toggleExpanded(step.title)
										}
									>
										<p>{`${
											showAllText !== step.title
												? "Show details"
												: "Hide details"
										}`}</p>
										<Chevron
											fill="#222"
											height={"20px"}
											width={"20px"}
											rotation={showAllText === step.title ? 0 : 180}
										/>
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionSteppedProcess;
